'use client'

import { OutlineButton, Text } from '@macarongfactory/web-ui'
import { useRef, useState } from 'react'

import { ExpandFooter } from '@/entities/place'
import { logEvent } from '@/shared/external/firebase/analytics'
import { useSeoOnelinkUrl } from '@/shared/external/onelink'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { cn } from '@/shared/lib/cn'

import {
  PLACE_CATEGORY_PRODUCTS,
  PLACE_DETAIL_PERFORMANCE_MORE_ONELINK_URL,
  REPLACEMENT_INTERVAL_BY_SECTION,
} from '../../lib'
import { useImpressionLog } from '../../model'
import { type IPlaceIntroduction } from '../../model'
import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PERFORMANCE,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.PERFORMANCE,
}
const EXPAND_BUTTON_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PERFORMANCE,
  type: TYPE.BUTTON,
  label: LABELS.COMMON_WEB_PLACE.EXPAND,
}
const APP_CTA_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PERFORMANCE,
  type: TYPE.BUTTON,
  label: LABELS.COMMON_WEB_PLACE.PERFORMANCE_MORE_BUTTON,
}
const APP_CTA_BUTTON_TEXT = '앱에서 시공 후기 자세히 보기'

const INITIAL_VISIBLE_COUNT = 5
const TOP_RANK_THRESHOLD = 3
const MAX_VISIBLE_ITEMS = 16

const SECTION_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  PLACE_CATEGORY_PRODUCTS.map(category => [category.section, category.name])
)

interface IPlacePerformanceProps {
  introduction: IPlaceIntroduction | null
  isPartnerEntry?: boolean
}

interface IPerformanceItem {
  rank: number
  section: string
  name: string
  intervalText: string
  count: number
}

const PlacePerformance = ({
  introduction,
  isPartnerEntry,
}: IPlacePerformanceProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const appOnelinkUrl = useSeoOnelinkUrl(
    PLACE_DETAIL_PERFORMANCE_MORE_ONELINK_URL
  )
  const sectionRef = useRef<HTMLElement | null>(null)
  const expandButtonRef = useRef<HTMLDivElement | null>(null)
  const appCtaRef = useRef<HTMLDivElement | null>(null)

  const totalCount = introduction?.diaryCount ?? 0
  const items: IPerformanceItem[] = (introduction?.productSections ?? [])
    .filter(section => Boolean(SECTION_NAME_BY_CODE[section.section]))
    .slice(0, MAX_VISIBLE_ITEMS)
    .map((section, index) => ({
      rank: index + 1,
      section: section.section,
      name: SECTION_NAME_BY_CODE[section.section],
      intervalText: REPLACEMENT_INTERVAL_BY_SECTION[section.section] ?? '',
      count: section.diaryCount ?? 0,
    }))

  const isSectionVisible =
    Boolean(introduction) && totalCount > 0 && items.length > 0
  const hasMore = items.length > INITIAL_VISIBLE_COUNT
  // 전체 리스트가 보일 때(5개 이하이거나 펼친 상태) 앱 유도 CTA 노출
  // 제휴 유입이면 앱 설치 유도 경로를 모두 없앤다 (WEB-5901)
  const showAppCta = !isPartnerEntry && (!hasMore || isExpanded)

  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
    enabled: isSectionVisible,
  })
  useImpressionLog({
    ref: expandButtonRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: EXPAND_BUTTON_PARAMS,
    enabled: isSectionVisible && hasMore && !isExpanded,
  })
  useImpressionLog({
    ref: appCtaRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: APP_CTA_PARAMS,
    enabled: isSectionVisible && showAppCta,
  })

  if (!isSectionVisible) return null

  const visibleItems = isExpanded
    ? items
    : items.slice(0, INITIAL_VISIBLE_COUNT)

  const handleExpandClick = () => {
    if (!isExpanded) {
      logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, EXPAND_BUTTON_PARAMS)
    }
    setIsExpanded(prev => !prev)
  }

  const handleAppCtaClick = () => {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, APP_CTA_PARAMS)
    window.open(appOnelinkUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        id='place-performance-section'
        aria-label='이 정비소 시공 실적'
        className={cn(
          'flex flex-col gap-dimension-x4 bg-background-surface-default px-dimension-x5 py-dimension-x6',
          // 제휴 유입은 고정 탑 네비게이션 높이만큼 스크롤 여백을 둬 섹션 상단이 가려지지 않게 한다 (WEB-5901)
          isPartnerEntry && 'scroll-mt-dimension-x14'
        )}
      >
        <div className='flex items-center gap-dimension-x1'>
          <Text
            as='h2'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
            className='whitespace-nowrap'
          >
            이 정비소 시공 실적
          </Text>
          <Text
            variant='title-1-semibold'
            color='text-foreground-brand-strong'
            className='min-w-0 flex-1'
          >
            {totalCount.toLocaleString()}
          </Text>
          {/* TODO: 피그마 Body/3_Medium 13/21. DS body-3 토큰 없어 body-2-medium(13/20)로 근사 */}
          <Text
            variant='body-2-medium'
            color='text-foreground-neutral-assistive'
            className='whitespace-nowrap'
          >
            시공 실적 많은순
          </Text>
        </div>

        <div className='flex flex-col gap-dimension-x4'>
          {visibleItems.map(item => (
            <PerformanceItem key={item.section} item={item} />
          ))}

          {!isPartnerEntry && hasMore && !isExpanded && (
            <div ref={expandButtonRef}>
              <ExpandFooter
                text='더보기'
                chevron='down'
                onClick={handleExpandClick}
              />
            </div>
          )}

          {showAppCta && (
            <div ref={appCtaRef}>
              <OutlineButton
                text={APP_CTA_BUTTON_TEXT}
                variant='neutral'
                size='medium'
                fullWidth
                clickable={handleAppCtaClick}
              />
            </div>
          )}

          {hasMore && isExpanded && (
            <ExpandFooter
              text='닫기'
              chevron='up'
              onClick={handleExpandClick}
            />
          )}
        </div>
      </section>
    </>
  )
}

interface IPerformanceItemProps {
  item: IPerformanceItem
}

const PerformanceItem = ({ item }: IPerformanceItemProps) => {
  const isTopRank = item.rank <= TOP_RANK_THRESHOLD

  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-dimension-x3'>
        <div
          className={cn(
            'flex size-[32px] shrink-0 items-center justify-center overflow-clip rounded-dimension-x2-5',
            isTopRank
              ? 'bg-background-brand-weak'
              : 'bg-background-surface-basement'
          )}
        >
          <Text
            variant='body-1-medium'
            color={
              isTopRank
                ? 'text-foreground-brand-contrast'
                : 'text-foreground-neutral-alternative'
            }
          >
            {item.rank}
          </Text>
        </div>
        <div className='flex w-[124px] flex-col items-start justify-center'>
          <Text
            variant='title-2-semibold'
            color='text-foreground-neutral-strong'
            className='whitespace-nowrap'
          >
            {item.name}
          </Text>
          <Text
            variant='caption-1-medium'
            color='text-foreground-neutral-alternative'
            className='whitespace-nowrap'
          >
            {item.intervalText}
          </Text>
        </div>
      </div>
      <Text
        variant='title-1-semibold'
        color={
          isTopRank
            ? 'text-foreground-brand-strong'
            : 'text-foreground-neutral-normal'
        }
        className='whitespace-nowrap'
      >
        {item.count.toLocaleString('ko-KR')}건
      </Text>
    </div>
  )
}

export default PlacePerformance
